import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import {
  MagnifyingGlassIcon,
  BookOpenIcon,
  ArrowRightIcon,
  DocumentTextIcon,
  FunnelIcon,
  RectangleStackIcon,
} from '@heroicons/react/24/outline'
import { useNavigate } from 'react-router-dom'
import { workspacesAPI, type Workspace } from '@/api/workspaces'
import { logError } from '@utils/logger'
import toast from 'react-hot-toast'
import clsx from 'clsx'

const DiscoverPage: React.FC = () => {
  const navigate = useNavigate()
  const [searchQuery, setSearchQuery] = useState('')
  const [showFilters, setShowFilters] = useState(false)
  const [recentWorkspaces, setRecentWorkspaces] = useState<Workspace[]>([])
  const [isLoadingWorkspaces, setIsLoadingWorkspaces] = useState(true)
  const [filters, setFilters] = useState({
    yearFrom: '',
    yearTo: '',
    maxResults: 50,
    customInstructions: '',
  })

  useEffect(() => {
    const loadRecentWorkspaces = async () => {
      try {
        const workspaces = await workspacesAPI.listWorkspaces()
        // Sort by created_at (newest first) and take top 6
        const sorted = (workspaces || []).sort((a, b) => {
          const aDate = a.created_at ? new Date(a.created_at).getTime() : 0
          const bDate = b.created_at ? new Date(b.created_at).getTime() : 0
          return bDate - aDate
        })
        setRecentWorkspaces(sorted.slice(0, 6))
      } catch (error) {
        logError('Error loading recent workspaces on Discover page:', error)
      } finally {
        setIsLoadingWorkspaces(false)
      }
    }

    loadRecentWorkspaces()
  }, [])

  const createWorkspaceAndNavigate = async () => {
    if (!searchQuery.trim()) {
      toast.error('Please enter a research question')
      return
    }

    try {
      const workspace = await workspacesAPI.createWorkspace({
        query: searchQuery.trim(),
        filters: {
          year_from: filters.yearFrom ? parseInt(filters.yearFrom) : undefined,
          year_to: filters.yearTo ? parseInt(filters.yearTo) : undefined,
          custom_instructions: filters.customInstructions.trim() || undefined,
        },
      })
      toast.success('Workspace created')
      navigate(`/workspaces/${workspace.workspace_id}`)
    } catch (error) {
      logError('Error creating workspace:', error)
      toast.error('Failed to create workspace')
    }
  }

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      return
    }
    
    // Create a workspace instead of navigating to search results
    await createWorkspaceAndNavigate()
  }

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch()
    }
  }

  return (
    <div className="h-full w-full overflow-y-auto bg-gradient-to-br from-slate-50 via-slate-100 to-slate-50 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <div className="flex justify-center mb-6">
            <div className="flex items-center justify-center w-16 h-16 bg-gradient-to-br from-teal-400 to-cyan-500 rounded-2xl shadow-lg">
              <BookOpenIcon className="w-8 h-8 text-white" />
            </div>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-slate-900 dark:text-white mb-4">
            <span className="bg-gradient-to-r from-teal-400 via-cyan-500 to-blue-500 bg-clip-text text-transparent">
              Discover
            </span>
          </h1>
          <p className="text-xl text-slate-600 dark:text-slate-400 max-w-2xl mx-auto">
            Explore workspaces, templates, and tools to jumpstart your scientific research and writing
          </p>
        </motion.div>

        {/* Search Bar */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.6 }}
          className="mb-8"
        >
          <div className="relative max-w-3xl mx-auto">
            <div className="flex gap-2">
              <div className="flex-1 relative">
                <MagnifyingGlassIcon className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
                <input
                  type="text"
                  placeholder="Enter your research question to create a workspace..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyPress={handleKeyPress}
                  className="w-full pl-12 pr-4 py-4 bg-white dark:bg-slate-800 border-2 border-slate-200 dark:border-slate-700 rounded-xl focus:outline-none focus:border-teal-500 dark:focus:border-teal-500 text-slate-900 dark:text-white placeholder-slate-400"
                />
              </div>
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={clsx(
                  'px-4 py-4 rounded-xl border-2 transition-all',
                  showFilters
                    ? 'border-teal-500 bg-teal-50 dark:bg-teal-900/20 text-teal-600 dark:text-teal-400'
                    : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:border-slate-300 dark:hover:border-slate-600'
                )}
                title="Filters"
              >
                <FunnelIcon className="w-5 h-5" />
              </button>
              <button
                onClick={handleSearch}
                disabled={!searchQuery.trim()}
                className={clsx(
                  'px-6 py-4 rounded-xl font-semibold transition-all flex items-center gap-2',
                  !searchQuery.trim()
                    ? 'bg-slate-200 dark:bg-slate-700 text-slate-400 dark:text-slate-500 cursor-not-allowed'
                    : 'bg-gradient-to-r from-teal-500 to-cyan-500 text-white hover:shadow-lg'
                )}
              >
                <MagnifyingGlassIcon className="w-5 h-5" />
                Search
              </button>
            </div>

            {/* Filters Panel */}
            {showFilters && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="mt-4 p-6 bg-white dark:bg-slate-800 rounded-xl border-2 border-slate-200 dark:border-slate-700 space-y-4"
              >
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                      Year From
                    </label>
                    <input
                      type="number"
                      value={filters.yearFrom}
                      onChange={(e) => setFilters({ ...filters, yearFrom: e.target.value })}
                      placeholder="e.g., 2020"
                      className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                      Year To
                    </label>
                    <input
                      type="number"
                      value={filters.yearTo}
                      onChange={(e) => setFilters({ ...filters, yearTo: e.target.value })}
                      placeholder="e.g., 2024"
                      className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                    Max Results
                  </label>
                  <input
                    type="number"
                    value={filters.maxResults}
                    onChange={(e) => setFilters({ ...filters, maxResults: parseInt(e.target.value) || 50 })}
                    min={1}
                    max={100}
                    className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                    Custom Instructions (Optional)
                  </label>
                  <textarea
                    value={filters.customInstructions}
                    onChange={(e) => setFilters({ ...filters, customInstructions: e.target.value })}
                    placeholder="e.g., Focus on recent deep learning papers, prioritize papers from top-tier conferences..."
                    rows={3}
                    className="w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white resize-none"
                  />
                </div>
              </motion.div>
            )}
          </div>
        </motion.div>

        {/* Recent Workspaces */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.35, duration: 0.6 }}
          className="mb-12"
        >
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-2xl font-bold text-slate-900 dark:text-white">
              Your recent workspaces
            </h2>
            <button
              onClick={() => navigate('/workspaces')}
              className="inline-flex items-center gap-2 text-sm font-medium text-teal-600 hover:text-teal-700 dark:text-teal-400 dark:hover:text-teal-300"
            >
              <span>View all</span>
              <ArrowRightIcon className="w-4 h-4" />
            </button>
          </div>

          {isLoadingWorkspaces ? (
            <div className="flex items-center justify-center py-6">
              <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-teal-500" />
            </div>
          ) : recentWorkspaces.length === 0 ? (
            <p className="text-slate-600 dark:text-slate-400 text-sm">
              You don&apos;t have any workspaces yet. Enter a research question above to create your first one.
            </p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {recentWorkspaces.map((ws) => (
                <button
                  key={ws.workspace_id}
                  onClick={() => navigate(`/workspaces/${ws.workspace_id}`)}
                  className="p-4 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-left hover:border-teal-400 hover:shadow-lg transition-all"
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <RectangleStackIcon className="w-5 h-5 text-teal-500" />
                      <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300">
                        {ws.status}
                      </span>
                    </div>
                    {ws.created_at && (
                      <span className="text-xs text-slate-500 dark:text-slate-400">
                        {new Date(ws.created_at).toLocaleDateString()}
                      </span>
                    )}
                  </div>
                  <h3 className="font-semibold text-slate-900 dark:text-white mb-1 line-clamp-2">
                    {ws.title || (ws.query ? `${ws.query.substring(0, 80)}${ws.query.length > 80 ? '…' : ''}` : 'Untitled workspace')}
                  </h3>
                  {ws.field && (
                    <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                      Field: {ws.field}
                    </p>
                  )}
                </button>
              ))}
            </div>
          )}
        </motion.div>

        {/* Templates CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 0.6 }}
        >
          <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-6">
            Templates
          </h2>
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 bg-white dark:bg-slate-800 rounded-2xl p-6 border border-slate-200 dark:border-slate-700">
            <div className="flex items-start gap-4">
              <div className="flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-br from-teal-500 to-cyan-500">
                <DocumentTextIcon className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
                  Start from a template
                </h3>
                <p className="text-sm text-slate-600 dark:text-slate-400 mt-1 max-w-xl">
                  Browse all LaTeX templates for papers, theses, lab reports, and more on the templates page.
                </p>
              </div>
            </div>
            <button
              onClick={() => navigate('/templates')}
              className="inline-flex items-center justify-center gap-2 px-5 py-3 bg-gradient-to-r from-teal-500 to-cyan-500 text-white font-medium rounded-xl shadow-md hover:shadow-lg transition-all"
            >
              <span>View all templates</span>
              <ArrowRightIcon className="w-4 h-4" />
            </button>
          </div>
        </motion.div>

        {/* My Library CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.45, duration: 0.6 }}
          className="mt-8"
        >
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 bg-slate-900 dark:bg-slate-900 rounded-2xl p-6 border border-slate-800 shadow-md">
            <div className="flex items-start gap-4">
              <div className="flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-400 to-teal-500">
                <BookOpenIcon className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white">
                  View your saved library
                </h3>
                <p className="text-sm text-slate-300 mt-1 max-w-xl">
                  Browse all articles you&apos;ve saved as evidence or added manually, across every workspace.
                </p>
              </div>
            </div>
            <button
              onClick={() => navigate('/library')}
              className="inline-flex items-center justify-center gap-2 px-5 py-3 bg-white text-slate-900 font-medium rounded-xl shadow-md hover:shadow-lg transition-all"
            >
              <span>Open my library</span>
              <ArrowRightIcon className="w-4 h-4" />
            </button>
          </div>
        </motion.div>
      </div>
    </div>
  )
}

export default DiscoverPage

